import { useCallback, useState, useSyncExternalStore } from "react";
import {
  getDeferredPrompt,
  promptInstall,
  subscribeDeferredPrompt,
} from "./deferredInstall";
import { createInstallDismissStore } from "./installDismissStore";
import { isIos, isMobile, isStandalone, isUnsupportedInAppBrowser } from "./installability";

const dismissStore = createInstallDismissStore();

export type InstallPromptMode = "native" | "ios";

export interface UseInstallPromptResult {
  visible: boolean;
  mode: InstallPromptMode;
  accept: () => Promise<"accepted" | "dismissed" | "unavailable">;
  dismiss: () => void;
  instructionsOpen: boolean;
  closeInstructions: () => void;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const deferredPrompt = useSyncExternalStore(subscribeDeferredPrompt, getDeferredPrompt, () => null);
  const [dismissed, setDismissed] = useState(dismissStore.isDismissed);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const ios = isIos();
  const mode: InstallPromptMode = ios ? "ios" : "native";

  const visible =
    !isStandalone() &&
    !dismissed &&
    !isUnsupportedInAppBrowser() &&
    isMobile() &&
    (ios ? true : deferredPrompt !== null);

  const accept = useCallback(async () => {
    if (ios) {
      setInstructionsOpen(true);
      return "unavailable" as const;
    }
    return promptInstall();
  }, [ios]);

  const dismiss = useCallback(() => {
    dismissStore.markDismissed();
    setDismissed(true);
  }, []);

  const closeInstructions = useCallback(() => {
    setInstructionsOpen(false);
  }, []);

  return { visible, mode, accept, dismiss, instructionsOpen, closeInstructions };
}
